import { ClickAwayListener } from "@mui/material";
import {
  CustomDate,
  DateArray,
  DateFilter,
  DateFilterQueryType,
  FilterGroupFilterOption,
  KnownDateFilterTimeFrame,
  PendingFilter,
} from "@redotech/redo-model/conversation-filters/conversation-filters";
import { assertNever, Tuple } from "@redotech/util/type";
import { memo, useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import { RedoCheckbox } from "../arbiter-components/checkbox/redo-checkbox";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
} from "../arbiter-components/filter-group/redo-filter-group";
import {
  RedoTimePicker,
  TimeOfDay,
} from "../arbiter-components/time/redo-time-picker";
import {
  ConversationFilterOptionToIcon,
  ConversationFilterOptionToName,
} from "../conversation-filters/conversation-filter-icons";
import { DateRangeValue } from "../date-range";
import { Dropdown } from "../dropdown";
import { SimpleFilterDropdown } from "../filters/simple-filter-dropdown";
import { Flex } from "../flex";
import { Text } from "../text";

// CSS imports must be ordered
import "react-time-picker/dist/TimePicker.css";
//
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
//
import "react-calendar/dist/Calendar.css";
//
import { useHandler } from "@redotech/react-util/hook";
import { RedoListItem } from "../arbiter-components/list/redo-list";
import { RedoListDropdown } from "../arbiter-components/list/redo-list-dropdown";
import "../date-range.module.css";

export const ConversationTableDateFilterGroup = memo(
  function ConversationTableDateFilterGroup({
    dateFilter: { query, value, customDate, type },
    setDateFilter,
    removeFilter,
    filterOption,
    openOnRender,
  }: {
    filterOption: FilterGroupFilterOption;
    dateFilter: DateFilter<any> | PendingFilter<DateFilter<any>>;
    setDateFilter(
      dateFilter: DateFilter<any> | PendingFilter<DateFilter<any>>,
    ): void;
    removeFilter(): void;
    openOnRender: boolean;
  }) {
    const [valueRef, setValueRef] = useState<HTMLButtonElement | null>(null);

    useEffect(() => {
      if (openOnRender && valueRef) {
        valueRef.click();
      }
    }, [openOnRender, valueRef]);

    function handleSetCustomDateValue(value: CustomDate) {
      const sortedDates = Array.isArray(value) ? sortDates(value) : value;
      setDateFilter({
        type,
        query,
        value: KnownDateFilterTimeFrame.CUSTOM,
        customDate: sortedDates,
      });
    }

    const handleSetTimeFrame = useHandler(
      ({
        item: newTimeFrameItem,
      }: {
        item: RedoListItem<KnownDateFilterTimeFrame>;
      }) => {
        const newTimeFrame = newTimeFrameItem.value;
        if (newTimeFrame === KnownDateFilterTimeFrame.CUSTOM) {
          setCustomDateDropdownOpen(true);
          return;
        }
        const oldTimeFrame = value;
        if (newTimeFrame === oldTimeFrame) {
          return;
        }
        setDateFilter({
          type,
          query,
          value: newTimeFrame,
          customDate: undefined,
        });
      },
    );

    const handleSetDateQueryType = useHandler(
      (newQueryType: DateFilterQueryType) => {
        const oldQueryType = query;
        if (newQueryType === oldQueryType) {
          return;
        }
        const newCustomDate =
          value === KnownDateFilterTimeFrame.CUSTOM
            ? wrangleCustomDateIntoProperForm(newQueryType, customDate)
            : undefined;
        setDateFilter({
          type,
          query: newQueryType,
          value,
          customDate: newCustomDate,
        });
      },
    );

    const [customDateDropdownOpen, setCustomDateDropdownOpen] = useState(false);

    const valueAnchor = (
      <RedoFilterDropdownAnchor
        color="primary"
        onClick={() => setValueDropdownOpen(!valueDropdownOpen)}
        ref={setValueRef}
        text={getValueString(value, customDate)}
        weight="medium"
      />
    );

    const [valueDropdownOpen, setValueDropdownOpen] = useState(false);

    const valueOptions: RedoListItem<KnownDateFilterTimeFrame>[] =
      useMemo(() => {
        return Object.values(KnownDateFilterTimeFrame).map((item) => {
          return {
            id: item,
            type: "text",
            text: timeFrameToText[item],
            value: item,
          };
        });
      }, []);

    const valueDropdown = (
      <RedoListDropdown
        dropdownAnchor={valueRef}
        dropdownOpen={valueDropdownOpen}
        items={valueOptions}
        itemSelected={handleSetTimeFrame}
        selectedItems={value || undefined}
        setDropdownOpen={setValueDropdownOpen}
        size="xs"
      />
    );

    return (
      <>
        {valueDropdown}
        <CustomDatePickerDropdown
          anchor={valueRef}
          customDate={customDate}
          open={customDateDropdownOpen}
          queryType={query}
          setCustomDate={handleSetCustomDateValue}
          setOpen={setCustomDateDropdownOpen}
        />
        <RedoFilterGroup
          Icon={ConversationFilterOptionToIcon[filterOption]}
          propertyName={ConversationFilterOptionToName[filterOption]}
          query={
            <SimpleFilterDropdown
              filterStyle="query"
              options={Object.values(DateFilterQueryType)}
              optionToFriendlyName={(item) => dateFilterQueryTypeToText[item]}
              setValue={handleSetDateQueryType}
              value={query}
            />
          }
          removeFilter={removeFilter}
          value={valueAnchor}
        />
      </>
    );
  },
);

export function CustomDatePickerDropdown({
  allowTimeOfDayCustomization = true,
  anchor,
  open,
  setOpen,
  customDate,
  setCustomDate,
  queryType,
}: {
  allowTimeOfDayCustomization?: boolean;
  anchor: HTMLElement | null;
  open: boolean;
  setOpen(open: boolean): void;
  customDate: CustomDate | undefined;
  setCustomDate(dates: CustomDate): void;
  queryType: DateFilterQueryType;
}) {
  const [timesOfDayFromPicker, setTimesOfDayFromPicker] = useState<
    TimeOfDay | Tuple<TimeOfDay, 2> | undefined
  >();

  const [localCustomDates, setLocalCustomDates] = useState<
    CustomDate | undefined
  >(customDate);

  useEffect(() => {
    setLocalCustomDates(customDate);
    setTimesOfDayFromPicker(undefined);
  }, [customDate]);

  const [customizeTimeOfDay, setCustomizeTimeOfDay] = useState(false);

  const timesOfDay: TimeOfDay | Tuple<TimeOfDay, 2> | undefined =
    useMemo(() => {
      if (!customizeTimeOfDay || !localCustomDates) {
        return undefined;
      }

      if (Array.isArray(localCustomDates)) {
        const timesOfDayFromDates = [
          localCustomDates[0],
          localCustomDates[1],
        ].map((date) => {
          return { hours: date.getHours(), minutes: date.getMinutes() };
        }) as Tuple<TimeOfDay, 2>;

        if (timesOfDayFromPicker) {
          if (Array.isArray(timesOfDayFromPicker)) {
            return timesOfDayFromPicker;
          } else {
            return [timesOfDayFromPicker, timesOfDayFromDates[1]];
          }
        } else {
          return timesOfDayFromDates;
        }
      } else {
        if (timesOfDayFromPicker) {
          if (Array.isArray(timesOfDayFromPicker)) {
            return timesOfDayFromPicker[0];
          }
          return timesOfDayFromPicker;
        }
        return {
          hours: localCustomDates.getHours(),
          minutes: localCustomDates.getMinutes(),
        };
      }
    }, [localCustomDates, timesOfDayFromPicker, customizeTimeOfDay]);

  function handleClose(datesToCloseWith?: CustomDate) {
    const dates = datesToCloseWith || localCustomDates;

    if (!dates) {
      setOpen(false);
      return;
    }

    if (!timesOfDay) {
      setCustomDate(dates);
      setOpen(false);
      return;
    }

    if (Array.isArray(timesOfDay) && Array.isArray(dates)) {
      const [startTime, endTime] = timesOfDay;
      const [startDate, endDate] = dates;
      const newCustomDate: [Date, Date] = [
        new Date(startDate),
        new Date(endDate),
      ];
      newCustomDate[0].setHours(startTime.hours, startTime.minutes);
      newCustomDate[1].setHours(endTime.hours, endTime.minutes);
      setCustomDate(newCustomDate);
    } else if (!Array.isArray(timesOfDay) && !Array.isArray(dates)) {
      const newCustomDate = new Date(dates);
      newCustomDate.setHours(timesOfDay.hours, timesOfDay.minutes);
      setCustomDate(newCustomDate);
    }

    setOpen(false);
  }

  function handleSetCustomizeTimeOfDay(customize: boolean) {
    if (!allowTimeOfDayCustomization) {
      return;
    }

    if (!customize) {
      setTimesOfDayFromPicker(undefined);
      setLocalCustomDates(
        wrangleDatePickerSubmission(queryType, localCustomDates || null),
      );
    }
    setCustomizeTimeOfDay(customize);
  }

  function handleSetCustomDate(value: DateRangeValue | CustomDate) {
    const formattedCustomDate = wrangleDatePickerSubmission(queryType, value);
    setLocalCustomDates(formattedCustomDate);
    if (!customizeTimeOfDay) {
      handleClose(formattedCustomDate);
    }
  }

  return (
    <>
      {open && (
        <ClickAwayListener onClickAway={() => handleClose()}>
          <Dropdown
            anchor={anchor}
            constrainHeight={false}
            fitToAnchor={false}
            open={open}
          >
            <Flex dir="column">
              <Calendar
                maxDate={new Date()}
                minDetail="year"
                onChange={handleSetCustomDate}
                selectRange={queryType === DateFilterQueryType.WITHIN}
                value={localCustomDates as any}
              />
              {allowTimeOfDayCustomization && (
                <>
                  <RedoCheckbox
                    label="Customize time of day"
                    setValue={(value) => handleSetCustomizeTimeOfDay(!!value)}
                    value={customizeTimeOfDay}
                  />
                  {customizeTimeOfDay &&
                    Array.isArray(localCustomDates) &&
                    Array.isArray(timesOfDay) && (
                      <RangeTimePicker
                        dates={localCustomDates}
                        setTimesOfDay={setTimesOfDayFromPicker}
                        timesOfDay={timesOfDay}
                      />
                    )}
                  {customizeTimeOfDay &&
                    localCustomDates &&
                    timesOfDay &&
                    !Array.isArray(localCustomDates) &&
                    !Array.isArray(timesOfDay) && (
                      <SingleDateTimePicker
                        date={localCustomDates}
                        setTimeOfDay={setTimesOfDayFromPicker}
                        timeOfDay={timesOfDay}
                      />
                    )}
                </>
              )}
            </Flex>
          </Dropdown>
        </ClickAwayListener>
      )}
    </>
  );
}

function dateToTimeOfDay(date: Date): TimeOfDay {
  return { hours: date.getHours(), minutes: date.getMinutes() };
}

function SingleDateTimePicker({
  date,
  timeOfDay,
  setTimeOfDay,
}: {
  date: Date;
  timeOfDay: TimeOfDay;
  setTimeOfDay(date: TimeOfDay): void;
}) {
  function handleSetStartTimeVal(newTimeOfDay: TimeOfDay | null) {
    const timeOfDayToSet = newTimeOfDay || dateToTimeOfDay(date);
    setTimeOfDay(timeOfDayToSet);
  }

  return (
    <Flex justify="space-between">
      <Text>{date.toDateString()}</Text>
      <Text> at </Text>
      <RedoTimePicker setValue={handleSetStartTimeVal} value={timeOfDay} />
    </Flex>
  );
}

function RangeTimePicker({
  dates,
  timesOfDay,
  setTimesOfDay,
}: {
  dates: Tuple<Date, 2>;
  timesOfDay: Tuple<TimeOfDay, 2>;
  setTimesOfDay(date: Tuple<TimeOfDay, 2>): void;
}) {
  function handleSetStartTimeVal(newTimeOfDay: TimeOfDay | null) {
    const timeOfDayToSet = newTimeOfDay || dateToTimeOfDay(dates[0]);
    setTimesOfDay([timeOfDayToSet, timesOfDay[1]]);
  }

  function handleSetEndTimeVal(newTimeOfDay: TimeOfDay | null) {
    const timeOfDayToSet = newTimeOfDay || dateToTimeOfDay(dates[1]);
    setTimesOfDay([timesOfDay[0], timeOfDayToSet]);
  }

  return (
    <Flex dir="column">
      <Flex justify="space-between">
        <Text>{dates[0].toDateString()}</Text>
        <Text> at </Text>
        <RedoTimePicker
          setValue={handleSetStartTimeVal}
          value={timesOfDay[0]}
        />
      </Flex>
      <Flex justify="space-between">
        <Text>{dates[1].toDateString()}</Text>
        <Text> at </Text>
        <RedoTimePicker setValue={handleSetEndTimeVal} value={timesOfDay[1]} />
      </Flex>
    </Flex>
  );
}

function formatDateTime(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options).replace(", ", " at ");
}

function getValueString(
  timeFrame: KnownDateFilterTimeFrame | null,
  dateRange: Date | Tuple<Date, 2> | undefined,
): string {
  if (!timeFrame) {
    return "...";
  }
  if (!dateRange || timeFrame !== KnownDateFilterTimeFrame.CUSTOM) {
    return timeFrameToText[timeFrame];
  }
  if (Array.isArray(dateRange)) {
    const [startDate, endDate] = dateRange;
    return `${formatDateTime(startDate)} - ${formatDateTime(endDate)}`;
  }
  return formatDateTime(dateRange);
}

export const timeFrameToText: Record<KnownDateFilterTimeFrame, string> = {
  [KnownDateFilterTimeFrame.TODAY]: "Today",
  [KnownDateFilterTimeFrame.THIS_WEEK]: "This Week",
  [KnownDateFilterTimeFrame.LAST_WEEK]: "Last Week",
  [KnownDateFilterTimeFrame.THIS_MONTH]: "This Month",
  [KnownDateFilterTimeFrame.LAST_MONTH]: "Last Month",
  [KnownDateFilterTimeFrame.THIS_YEAR]: "This Year",
  [KnownDateFilterTimeFrame.LAST_YEAR]: "Last Year",
  [KnownDateFilterTimeFrame.CUSTOM]: "Custom",
};

export const dateFilterQueryTypeToText: Record<DateFilterQueryType, string> = {
  [DateFilterQueryType.WITHIN]: "during",
  [DateFilterQueryType.BEFORE]: "before",
  [DateFilterQueryType.AFTER]: "after",
};

export function sortDates(dates: DateArray): DateArray {
  return dates.sort((a, b) => a.getTime() - b.getTime());
}

export function wrangleDatePickerSubmission(
  queryType: DateFilterQueryType,
  value: DateRangeValue | CustomDate,
): CustomDate {
  if (Array.isArray(value)) {
    const [startDate, endDate] = value;
    const definedStartDate = startDate || new Date();
    const definedEndDate = endDate || new Date();
    const newCustomDateRange = sortDates([definedStartDate, definedEndDate]);

    const newCustomDate = wrangleCustomDateIntoProperForm(
      queryType,
      newCustomDateRange,
    );

    return newCustomDate;
  } else if (value) {
    const newCustomDate = wrangleCustomDateIntoProperForm(queryType, value);
    return newCustomDate;
  } else {
    const newCustomDate = wrangleCustomDateIntoProperForm(
      queryType,
      new Date(),
    );
    return newCustomDate;
  }
}

export function wrangleCustomDateIntoProperForm(
  queryType: DateFilterQueryType,
  customDate: CustomDate | undefined,
): CustomDate {
  switch (queryType) {
    case DateFilterQueryType.WITHIN: {
      if (Array.isArray(customDate)) {
        return customDate;
      } else if (customDate) {
        const startOfDay = new Date(customDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(customDate);
        endOfDay.setHours(23, 59, 59, 999);
        return [startOfDay, endOfDay];
      } else {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        return [startOfDay, endOfDay];
      }
    }
    case DateFilterQueryType.BEFORE: {
      if (Array.isArray(customDate)) {
        const beforeDate = new Date(customDate[0]);
        beforeDate.setHours(0, 0, 0, 0);
        return beforeDate;
      } else if (customDate) {
        const beforeDate = new Date(customDate);
        beforeDate.setHours(0, 0, 0, 0);
        return beforeDate;
      } else {
        const beforeDate = new Date();
        beforeDate.setHours(0, 0, 0, 0);
        return beforeDate;
      }
    }
    case DateFilterQueryType.AFTER: {
      if (Array.isArray(customDate)) {
        const afterDate = new Date(customDate[1]);
        afterDate.setHours(23, 59, 59, 999);
        return afterDate;
      } else if (customDate) {
        const afterDate = new Date(customDate);
        afterDate.setHours(23, 59, 59, 999);
        return afterDate;
      } else {
        const afterDate = new Date();
        afterDate.setHours(23, 59, 59, 999);
        return afterDate;
      }
    }
    default: {
      assertNever(queryType);
    }
  }
}
